// ↩ Refund — the one refund dialog (moved from the Billing tab). Posts the
// existing refund direction to /orders/:id/collect. The server's guards
// (design §6) are mirrored here: beyond the overpayment, or on a correctly
// paid order, the refund must be explicit; a card-paid order is refused until
// Stripe Connect exists. Refunds send no receipt (the dialog says so).
import React, { useState } from 'react';
import axios from '../../lib/axiosClient';
import { UI, fmtMoney, useIsNarrow } from './pipeline';

export default function RefundDialog({ order, onClose, onDone, showFlash }) {
  const narrow = useIsNarrow();
  const pay = order.payment || {};
  const max = Number(order.amount_paid || 0);
  const owed = Number(pay.owed_to_client || 0);
  const cancelledWithMoney = order.status === 'cancelled' && max > 0;
  const [amount, setAmount] = useState(owed > 0 ? owed.toFixed(2) : cancelledWithMoney ? max.toFixed(2) : '');
  const [method, setMethod] = useState(null);
  const [note, setNote] = useState(owed > 0 ? 'Refund — overpayment' : cancelledWithMoney ? 'Refund — cancelled order' : '');
  const [beyond, setBeyond] = useState(false);   // explicit: refund beyond the overpayment / on a paid order
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const amt = Number(amount);
  const needsFlag = !cancelledWithMoney && amt > owed + 0.005;
  const ready = amt > 0 && amt <= max + 0.005 && !!method && (!needsFlag || beyond) && !busy;

  async function go() {
    setBusy(true); setErr(null);
    try {
      await axios.post(`/api/rentals/orders/${order.id}/collect`, { amount: amt, method, note: note || 'Refund', direction: 'refund', send_receipt: false, allow_partial_refund_of_paid: needsFlag && beyond });
      onDone();
    } catch (e) {
      const d = e.response?.data || {};
      setErr(d.error || 'Failed'); setBusy(false);
      showFlash?.(d.error || 'Refund failed', 'error');
    }
  }

  return (
    <div style={UI.overlay(narrow)} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={UI.box(narrow, 440)} data-testid="refund-dialog" role="dialog">
        <div style={UI.head}>
          <div><div style={UI.title}>↩ Refund {order.order_number || '#' + order.id}</div><div style={UI.sub}>{order.client_name} · collected {fmtMoney(max)} on {fmtMoney(order.total)}</div></div>
          <button style={UI.close} onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div style={UI.body}>
          {owed > 0
            ? <div data-testid="refund-owed" style={{ ...UI.row, color: '#a78bfa', fontWeight: 700 }}><span>Client is owed</span><span>{fmtMoney(owed)}</span></div>
            : cancelledWithMoney
              ? <div style={{ ...UI.row, color: '#fbbf24', fontWeight: 700 }}><span>Cancelled with money held</span><span>{fmtMoney(max)}</span></div>
              : <div style={{ ...UI.row, color: '#94a3b8' }}><span>Nothing is owed on this order</span><span>paid {fmtMoney(max)}</span></div>}
          <label style={UI.lbl}>Refund amount</label>
          <input data-testid="refund-amount" style={{ ...UI.inp, fontSize: 20, fontWeight: 800, color: '#f87171' }} type="number" min="0" max={max} step="0.01" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" />
          {amt > max + 0.005 && <div style={{ fontSize: 11, color: '#f87171', marginTop: 4 }}>More than the {fmtMoney(max)} collected.</div>}
          {needsFlag && amt <= max + 0.005 && (
            <label data-testid="refund-beyond" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, color: '#fbbf24', cursor: 'pointer' }}>
              <input type="checkbox" checked={beyond} onChange={e => setBeyond(e.target.checked)} />
              {owed > 0 ? `This is more than the ${fmtMoney(owed)} owed — refund beyond the overpayment on purpose` : 'This order is not overpaid — refund anyway (e.g. a payment recorded in error)'}
            </label>
          )}
          <label style={UI.lbl}>Method {!method && <span style={{ color: '#f59e0b', fontWeight: 400, textTransform: 'none' }}>— choose one</span>}</label>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {['cash', 'card', 'check', 'venmo', 'zelle', 'other'].map(m => (
              <button key={m} type="button" data-testid={`refund-method-${m}`} onClick={() => setMethod(m)}
                style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${method === m ? '#f97316' : '#3a3f60'}`, background: method === m ? 'rgba(249,115,22,0.15)' : 'transparent', color: method === m ? '#f97316' : '#e2e8f0', fontSize: 12, cursor: 'pointer', textTransform: 'capitalize' }}>{m}</button>
            ))}
          </div>
          <label style={UI.lbl}>Note</label>
          <input style={UI.inp} value={note} onChange={e => setNote(e.target.value)} placeholder="Why the money goes back" />
          <label style={UI.lbl}>What happens</label>
          <div style={UI.effect}>• Writes a refund row for {amt > 0 ? fmtMoney(amt) : '…'}; the ledger keeps every row</div>
          <div style={UI.effect}>• Payment status becomes what the ledger says ({amt > 0 ? (Math.abs(max - amt - Number(order.total || 0)) < 0.005 ? 'paid' : max - amt > Number(order.total || 0) + 0.005 ? 'still overpaid' : max - amt > 0.005 ? 'partially paid' : 'payment due') : '…'})</div>
          <div style={UI.effect}>• No receipt is sent for refunds</div>
          {err && <div style={{ marginTop: 10, color: '#f87171', fontSize: 12 }} data-testid="dialog-error">{err}</div>}
        </div>
        <div style={UI.foot}>
          <button style={UI.cancelBtn} onClick={onClose}>Back</button>
          <button data-testid="refund-confirm" disabled={!ready} onClick={go} style={UI.goBtn(ready, '#3b1a1a', '#fca5a5')}>
            {busy ? 'Refunding…' : !method ? 'Choose a method' : !(amt > 0) ? 'Enter an amount' : needsFlag && !beyond ? 'Confirm the checkbox above' : `↩ Refund ${fmtMoney(amt)} ${method}`}
          </button>
        </div>
      </div>
    </div>
  );
}
